import * as React from "react";

import { UISimpleCondition } from "@/shared/components/filters";
import { AutocompleteType } from "@/shared/components/TagAndMetadataAutocomplete";
import { useElementsForAutocompleteType } from "@/shared/components/TagAndMetadataAutocomplete/hook";
import { TriggerForEachPrimitive } from "@/shared/domain/actions/TriggerRecord";
import { ConditionsOperator } from "@/types";

import {
  TriggerConditionForm,
  TriggerCondition as TriggerConditionType,
} from "./TriggerConditionForm";
import { parseFieldName } from "./TriggerConditionForm/utils";
import { searchFields } from "./types";

interface TriggerConditionProps {
  conditions: UISimpleCondition[];
  setConditions?: (arg: UISimpleCondition[]) => void;
  operator: ConditionsOperator;
  setOperator?: (arg: ConditionsOperator) => void;
  editable: boolean;
  forEachPrimitive: TriggerForEachPrimitive;
}

function uiSimpleToTriggerCondition(
  uiCondition: UISimpleCondition,
): TriggerConditionType {
  const { prefix } = parseFieldName(uiCondition.field);
  return {
    id: uiCondition.id,
    resourcePrefix: prefix,
    field: uiCondition.field,
    comparator: uiCondition.comparator as string,
    value: String(uiCondition.value),
    searchFieldType: uiCondition.searchFieldType,
  };
}

function triggerConditionToUISimple(
  triggerCondition: TriggerConditionType,
): UISimpleCondition {
  return {
    id: triggerCondition.id,
    field: triggerCondition.field,
    comparator: triggerCondition.comparator as UISimpleCondition["comparator"],
    value: triggerCondition.value,
    searchFieldType: triggerCondition.searchFieldType,
  };
}

export const TriggerCondition: React.FC<TriggerConditionProps> = ({
  conditions,
  setConditions,
  operator,
  setOperator,
  editable,
  forEachPrimitive,
}) => {
  const { elements: metadataKeys } = useElementsForAutocompleteType(
    AutocompleteType.DatasetMetadataKeys,
  );

  const triggerConditions = React.useMemo(
    () => conditions.map(uiSimpleToTriggerCondition),
    [conditions],
  );

  const handleConditionsChange = (newConditions: TriggerConditionType[]) => {
    if (setConditions) {
      const uiConditions = newConditions.map(triggerConditionToUISimple);
      setConditions(uiConditions);
    }
  };

  const handleOperatorChange = (newOperator: ConditionsOperator) => {
    if (setOperator) {
      setOperator(newOperator);
    }
  };

  return (
    <TriggerConditionForm
      conditions={triggerConditions}
      onConditionsChange={handleConditionsChange}
      operator={operator}
      onOperatorChange={handleOperatorChange}
      forEachPrimitive={forEachPrimitive}
      searchFields={searchFields}
      editable={editable}
      availableMetadataKeys={metadataKeys}
    />
  );
};
